import { c as _c } from "react/compiler-runtime";
import { createRef, useCallback, useEffect, useState, type FC } from 'react';
import { captureException } from '@sentry/browser';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import { batch, connect } from 'react-redux';
import { useDebounce } from '@sbt-web/hooks';
import { getHereGeocode } from '@sbt-web/network/client';
import { FilterURI, HereGeocodeItem } from '@sbt-web/network/types';
import { Pulse } from '@sbt-web/tracking';
import { Button, FormGroup, FullModal, Slider, TextField } from '@sbt-web/ui';
import { formatDistance } from '@sbt-web/utils';
import type { RootState } from '@/reducers/index';
import { RadiusSearch, setRadiusDialogStatus, updateRadiusSearch } from '@/reducers/search';
import { setPreventPopstate } from '@/reducers/system';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { HERE_MAPS_BASE_URL } from '@/shared/constants';
import { FilterLabel } from '../common/FilterLabel';
import type { CommonFilterProps } from '../common/types';
import FilterMap from './FilterMap';
import SearchMap from './SearchMap';
import { buildRadiusSearchCancelEvent, buildRadiusSearchInitiateEvent } from './tracking';
import ownClasses from './index.module.scss';
const defaultRadius = 1000;
interface MapModalProps {
  handleSelection: (selection: RadiusSearch) => void;
  isOpen: boolean;
  onClose: () => void;
  radiusSearch?: RadiusSearch;
}
interface RadiusPickerProps {
  enabled: boolean;
  startingRadius?: number;
  setRadius: (radius: number) => void;
}
interface StateProps {
  radiusSearch?: RadiusSearch;
}
interface DispatchProps {
  updateRadiusParameters: (radiusSearch: RadiusSearch) => void;
  updateRadiusDialogStatus: (isOpen: boolean) => void;
}
interface InputProps {
  value: string;
  name?: string;
  onSelect: (item: HereGeocodeItem) => void;
  pulse?: Pulse;
}
interface OwnProps {
  trackSearchConversion: () => void;
}
type Props = CommonFilterProps & StateProps & DispatchProps & OwnProps;
const useHere = (hereApiKey, debounceTimeout) => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [items, setItems] = useState(t0);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, debounceTimeout);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = t2 => {
      const q = t2 === undefined ? "" : t2;
      setQuery(q);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateQuery = t1;
  let t2;
  let t3;
  if ($[2] !== debouncedQuery || $[3] !== hereApiKey) {
    t2 = () => {
      if (debouncedQuery.length >= 2) {
        getHereGeocode(HERE_MAPS_BASE_URL, debouncedQuery, hereApiKey).then(res => {
          if (res.body?.payload) {
            setItems(res.body.payload);
          } else {
            throw new Error("Here payload was empty.");
          }
        }).catch(console.error);
      } else {
        setItems([]);
      }
    };
    t3 = [hereApiKey, debouncedQuery];
    $[2] = debouncedQuery;
    $[3] = hereApiKey;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== items) {
    t4 = [items, updateQuery];
    $[6] = items;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const textFieldRef = createRef<HTMLInputElement>();
const debounceTimeout = 250;
const GeoSearchInput = t0 => {
  const $ = _c(54);
  const {
    value,
    name,
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [options, setOptions] = useState(t1);
  const [hereItems, updateHereItems] = useHere(process.env.NEXT_PUBLIC_HERE_WEB_KEY, debounceTimeout);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp, t2);
  let t3;
  let t4;
  if ($[2] !== hereItems) {
    t3 = () => {
      setOptions(hereItems);
    };
    t4 = [hereItems, setOptions];
    $[2] = hereItems;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== updateHereItems) {
    t5 = changes => {
      updateHereItems(changes.inputValue);
    };
    $[5] = updateHereItems;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== onSelect) {
    t6 = t7 => {
      const {
        selectedItem
      } = t7;
      if (selectedItem == null) {
        return;
      }
      textFieldRef.current?.blur();
      onSelect(selectedItem);
    };
    $[7] = onSelect;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== options || $[10] !== t5 || $[11] !== t6 || $[12] !== value) {
    t7 = {
      id: "desktop-modal-geo-search",
      items: options,
      initialInputValue: value,
      itemToString: _temp2,
      onInputValueChange: t5,
      onSelectedItemChange: t6,
      onStateChange: _temp3
    };
    $[9] = options;
    $[10] = t5;
    $[11] = t6;
    $[12] = value;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    inputValue,
    isOpen,
    openMenu,
    setInputValue
  } = useCombobox(t7);
  let t8;
  if ($[14] !== setInputValue) {
    t8 = () => {
      setInputValue("");
      textFieldRef.current?.focus();
    };
    $[14] = setInputValue;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const clearInput = t8;
  let t9;
  if ($[16] !== getComboboxProps) {
    t9 = getComboboxProps();
    $[16] = getComboboxProps;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = inputValue?.length > 0;
  let t11;
  if ($[18] !== setInputValue) {
    t11 = e => setInputValue(e.target.value);
    $[18] = setInputValue;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== openMenu) {
    t12 = () => {
      openMenu();
    };
    $[20] = openMenu;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== getInputProps || $[23] !== inputValue || $[24] !== name || $[25] !== t12) {
    t13 = getInputProps({
      ref: textFieldRef,
      type: "search",
      placeholder: "Indirizzo, quartiere, citt\xE0, o CAP",
      name,
      value: inputValue,
      onFocus: t12
    });
    $[22] = getInputProps;
    $[23] = inputValue;
    $[24] = name;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== clearInput || $[28] !== t10 || $[29] !== t11 || $[30] !== t13) {
    t14 = <TextField size="large" icon="Pin" withClearIcon={t10} onInputChange={t11} onCleared={clearInput} disableNativeAC={true} {...t13} />;
    $[27] = clearInput;
    $[28] = t10;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t9) {
    t15 = <div {...t9}>{t14}</div>;
    $[32] = t14;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const t16 = isOpen && options.length > 0;
  let t17;
  if ($[35] !== t16) {
    t17 = classNames(ownClasses["dropdown-wrapper"], {
      [ownClasses.open]: t16
    });
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classNames(ownClasses.dropdown, ownClasses.results);
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== getMenuProps) {
    t19 = getMenuProps();
    $[38] = getMenuProps;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== getItemProps || $[41] !== highlightedIndex || $[42] !== isOpen || $[43] !== options) {
    t20 = isOpen && options.map((item_0, index) => <li className={classNames(ownClasses.item, ownClasses["size-small"], {
      [ownClasses.selected]: highlightedIndex === index
    })} key={`${item_0.title}${index}`} {...getItemProps({
      item: item_0,
      index
    })}>{item_0.title}</li>);
    $[40] = getItemProps;
    $[41] = highlightedIndex;
    $[42] = isOpen;
    $[43] = options;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t19 || $[46] !== t20) {
    t21 = <ul className={t18} {...t19}>{t20}</ul>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t17 || $[49] !== t21) {
    t22 = <div className={t17}>{t21}</div>;
    $[48] = t17;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t15 || $[52] !== t22) {
    t23 = <div className={ownClasses["geo-search-wrapper"]}>{t15}{t22}</div>;
    $[51] = t15;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
};
const MapModal: FC<MapModalProps> = t0 => {
  const $ = _c(42);
  const {
    radiusSearch,
    handleSelection,
    isOpen,
    onClose
  } = t0;
  const [prevSelection, setPrevSelection] = useState(undefined);
  let t1;
  if ($[0] !== prevSelection || $[1] !== radiusSearch) {
    t1 = () => {
      if (radiusSearch != undefined) {
        return {
          ...(prevSelection ?? {}),
          title: prevSelection?.title ?? "Il punto che hai selezionato",
          position: {
            lat: radiusSearch?.center.lat,
            lng: radiusSearch?.center.lng
          },
          address: prevSelection?.address ?? {}
        };
      } else {
        return;
      }
    };
    $[0] = prevSelection;
    $[1] = radiusSearch;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selectedPoint, setSelectedPoint] = useState(t1);
  let t2;
  if ($[3] !== radiusSearch) {
    t2 = () => {
      if (radiusSearch?.radiusMeters) {
        return radiusSearch.radiusMeters;
      } else {
        return defaultRadius;
      }
    };
    $[3] = radiusSearch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [radius, setRadius] = useState(t2);
  let t3;
  let t4;
  if ($[5] !== radiusSearch) {
    t3 = () => {
      if (!radiusSearch) {
        setPrevSelection(undefined);
        setSelectedPoint(undefined);
      }
    };
    t4 = [radiusSearch];
    $[5] = radiusSearch;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== prevSelection || $[9] !== radiusSearch || $[10] !== selectedPoint) {
    t5 = () => {
      if (radiusSearch && prevSelection == undefined) {
        setPrevSelection(selectedPoint);
      }
    };
    t6 = [radiusSearch, prevSelection, selectedPoint];
    $[8] = prevSelection;
    $[9] = radiusSearch;
    $[10] = selectedPoint;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  const [searchApplied, setSearchApplied] = useState(false);
  let t7;
  if ($[13] !== onClose || $[14] !== prevSelection || $[15] !== searchApplied) {
    t7 = () => {
      setSelectedPoint(prevSelection);
      onClose();
      if (searchApplied) {
        setSearchApplied(false);
      } else {
        getOrCreatePulse()?.queueEvent(buildRadiusSearchCancelEvent());
      }
    };
    $[13] = onClose;
    $[14] = prevSelection;
    $[15] = searchApplied;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = selectedPoint == undefined;
  let t9;
  if ($[17] !== handleSelection || $[18] !== onClose || $[19] !== radius || $[20] !== selectedPoint) {
    t9 = () => {
      if (selectedPoint != undefined && selectedPoint.position != undefined) {
        handleSelection({
          radiusMeters: radius,
          center: {
            lat: selectedPoint.position.lat,
            lng: selectedPoint.position.lng
          },
          friendlyName: selectedPoint.title,
          addressToTrack: selectedPoint.address
        });
        setPrevSelection(selectedPoint);
        setSearchApplied(true);
        onClose();
      } else {
        captureException(new Error("The selected point in the desktop dialog was undefined when applying the search."), {
          level: "error"
        });
        console.error("The selected point in the desktop dialog was undefined when applying the search.");
      }
    };
    $[17] = handleSelection;
    $[18] = onClose;
    $[19] = radius;
    $[20] = selectedPoint;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = [<Button key="confirm-radius" design="solid" disabled={t8} onClick={t9}>Applica</Button>];
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = selectedPoint?.title ?? "";
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = getOrCreatePulse();
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11) {
    t13 = <GeoSearchInput value={t11} onSelect={setSelectedPoint} name={FilterURI.Geo} pulse={t12} />;
    $[26] = t11;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== radius || $[29] !== selectedPoint) {
    t14 = <SearchMap radius={radius} selectedPoint={selectedPoint} />;
    $[28] = radius;
    $[29] = selectedPoint;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = radiusSearch?.radiusMeters;
  let t16;
  if ($[31] !== t15) {
    t16 = <RadiusPicker startingRadius={t15} setRadius={setRadius} enabled={true} />;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t16) {
    t17 = <div className={ownClasses["modal-content-wrapper"]}>{t13}{t14}{t16}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== isOpen || $[38] !== t10 || $[39] !== t17 || $[40] !== t7) {
    t18 = <FullModal open={isOpen} size="small" withClose={true} onClose={t7} title="Dove stai cercando?" actions={t10}>{t17}</FullModal>;
    $[37] = isOpen;
    $[38] = t10;
    $[39] = t17;
    $[40] = t7;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};
const RadiusPicker = t0 => {
  const $ = _c(11);
  const {
    enabled,
    startingRadius: t1,
    setRadius
  } = t0;
  const startingRadius = t1 === undefined ? 1000 : t1;
  let t2;
  if ($[0] !== setRadius) {
    t2 = newRadius => {
      setRadius(newRadius);
    };
    $[0] = setRadius;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onRadiusChange = t2;
  let t3;
  let t4;
  if ($[2] !== setRadius || $[3] !== startingRadius) {
    t3 = () => {
      setRadius(startingRadius);
    };
    t4 = [setRadius, startingRadius];
    $[2] = setRadius;
    $[3] = startingRadius;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className={classNames("body-text semibold", ownClasses["radius-picker-label"])}>Seleziona il raggio</p>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !enabled;
  let t7;
  if ($[7] !== onRadiusChange || $[8] !== startingRadius || $[9] !== t6) {
    t7 = <div className={ownClasses["radius-picker"]}>{t5}<Slider disabled={t6} min={500} max={10000} defaultValue={startingRadius} step={500} onChangeValue={onRadiusChange} labelBuilder={formatDistance} withLabel={true} /></div>;
    $[7] = onRadiusChange;
    $[8] = startingRadius;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
const RadiusSelector: FC<Props> = t0 => {
  const $ = _c(29);
  const {
    radiusSearch,
    showBadgeNew,
    updateRadiusParameters,
    updateRadiusDialogStatus,
    trackSearchConversion
  } = t0;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  let t1;
  if ($[0] !== trackSearchConversion || $[1] !== updateRadiusParameters) {
    t1 = selection => {
      updateRadiusParameters(selection);
      trackSearchConversion();
    };
    $[0] = trackSearchConversion;
    $[1] = updateRadiusParameters;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const applyRadius = t1;
  const ctaLabel = radiusSearch != undefined ? "Modifica raggio" : "Cerca per distanza";
  let t2;
  if ($[3] !== showBadgeNew) {
    t2 = <FilterLabel showBadgeNew={showBadgeNew} className={ownClasses["radius-filter-container"]}>Distanza da un punto</FilterLabel>;
    $[3] = showBadgeNew;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = radiusSearch?.radiusMeters ?? 1000;
  let t4;
  if ($[5] !== radiusSearch) {
    t4 = radiusSearch != undefined ? {
      position: {
        lat: radiusSearch.center.lat,
        lng: radiusSearch.center.lng
      }
    } : undefined;
    $[5] = radiusSearch;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <FilterMap radius={t3} selectedPoint={t4} />;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ctaLabel || $[11] !== updateRadiusDialogStatus) {
    t6 = () => {
      updateRadiusDialogStatus(true);
      setIsDialogOpen(true);
      getOrCreatePulse()?.queueEvent(buildRadiusSearchInitiateEvent({
        label: ctaLabel
      }));
    };
    $[10] = ctaLabel;
    $[11] = updateRadiusDialogStatus;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [ownClasses["dialog-entry-point"]];
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== ctaLabel || $[15] !== t6) {
    t8 = <Button design="solid-lite" onClick={t6} classes={t7}>{ctaLabel}</Button>;
    $[14] = ctaLabel;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== updateRadiusDialogStatus) {
    t9 = () => {
      updateRadiusDialogStatus(false);
      setIsDialogOpen(false);
    };
    $[17] = updateRadiusDialogStatus;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== applyRadius || $[20] !== isDialogOpen || $[21] !== radiusSearch || $[22] !== t9) {
    t10 = <MapModal handleSelection={applyRadius} isOpen={isDialogOpen} onClose={t9} radiusSearch={radiusSearch} />;
    $[19] = applyRadius;
    $[20] = isDialogOpen;
    $[21] = radiusSearch;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t2 || $[26] !== t5 || $[27] !== t8) {
    t11 = <FormGroup>{t2}{t5}{t8}{t10}</FormGroup>;
    $[24] = t10;
    $[25] = t2;
    $[26] = t5;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};
const ConnectedRadiusSelector = connect<StateProps, DispatchProps, OwnProps, RootState>((state, ownProps) => ({
  radiusSearch: state.search.radiusSearch || undefined,
  trackSearchConversion: ownProps.trackSearchConversion
}), dispatch => ({
  updateRadiusParameters: (radiusSearch: RadiusSearch) => {
    batch(() => {
      dispatch(setPreventPopstate(false));
      dispatch(setRadiusDialogStatus(false));
      dispatch(updateRadiusSearch({
        radiusSearch,
        isFinalSearch: true
      }));
    });
  },
  updateRadiusDialogStatus: (isOpen: boolean) => {
    batch(() => {
      dispatch(setPreventPopstate(isOpen));
      dispatch(setRadiusDialogStatus(isOpen));
    });
  }
}))(RadiusSelector);
ConnectedRadiusSelector.displayName = 'RadiusSelector';
export default ConnectedRadiusSelector;
function _temp() {
  textFieldRef.current?.focus();
}
function _temp2(item) {
  return item?.title ?? "";
}
function _temp3(changes_0) {
  if (changes_0.type === useCombobox.stateChangeTypes.ItemClick && changes_0.selectedItem == null) {
    textFieldRef.current?.blur();
  }
}